import type { ComponentProps, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { nanoid } from 'nanoid/non-secure';

import { NoticeBannersStackContext } from './NoticeBannersStackContext';

import type { NoticeBanner } from '../NoticeBanner';

//
// ─── CONTEXT ────────────────────────────────────────────────────────────────────
//

/**
 * The actions live in their own context so components that only dispatch
 * banners are not re-rendered every time the stack changes.
 */
const NoticeBannersStackActionsContext = createContext<
  NoticeBannersStackActionsContextValue | undefined
>(undefined);

export const NoticeBannersStackProvider = (props: NoticeBannersStackProviderProps) => {
  const { children, initialBanners = [] } = props;

  const [isEnabled, setIsEnabled] = useState(true);
  const [stack, setStack] = useState<NoticeBannersStackItems>(initialBanners);

  // ────────────────────────────────────────────────────────────────────

  const push = useCallback<NoticeBannersStackActionsContextValue['push']>(
    (banner, preventDuplicates) => {
      if (!isEnabled) return;

      setStack((currentStack) => {
        const bannerAlreadyExists =
          preventDuplicates && checkIfBannerAlreadyExists(currentStack, banner.id);

        if (bannerAlreadyExists) return currentStack;

        return [{ id: nanoid(), ...banner }, ...currentStack];
      });
    },
    [isEnabled],
  );

  const pop = useCallback<NoticeBannersStackActionsContextValue['pop']>(() => {
    setStack((currentStack) => currentStack.slice(1));
  }, []);

  const clear = useCallback<NoticeBannersStackActionsContextValue['clear']>(() => {
    setStack([]);
  }, []);

  const remove = useCallback<NoticeBannersStackActionsContextValue['remove']>((id: string) => {
    setStack((currentStack) => currentStack.filter((banner) => banner.id !== id));
  }, []);

  const enable = useCallback<NoticeBannersStackActionsContextValue['enable']>(() => {
    setIsEnabled(true);
  }, []);

  const disable = useCallback<NoticeBannersStackActionsContextValue['disable']>(() => {
    setIsEnabled(false);
  }, []);

  // ────────────────────────────────────────────────────────────────────

  const actionsValue = useMemo(
    () => ({ push, pop, remove, clear, enable, disable }),
    [push, pop, remove, clear, enable, disable],
  );

  return (
    <NoticeBannersStackActionsContext.Provider value={actionsValue}>
      <NoticeBannersStackContext.Provider value={stack}>
        {children}
      </NoticeBannersStackContext.Provider>
    </NoticeBannersStackActionsContext.Provider>
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

export const useNoticeBannersStackActions = () => {
  const context = useContext(NoticeBannersStackActionsContext);

  if (context === undefined) {
    throw new Error(
      'useNoticeBannersStackActions must be used within a <NoticeBannersStackProvider>',
    );
  }

  return context;
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const checkIfBannerAlreadyExists = (currentStack: NoticeBannersStackItems, bannerId = '') => {
  return currentStack.some((stackBanner) => stackBanner.id === bannerId);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type NoticeBannersStackProviderProps = Readonly<{
  children: ReactNode;
  initialBanners?: NoticeBannersStackItems;
}>;

type NoticeBannersStackActionsContextValue = Readonly<{
  push: (banner: NoticeBannerStackItem, preventDuplicates?: boolean) => void;
  pop: () => void;
  remove: (id: string) => void;
  clear: () => void;
  disable: () => void;
  enable: () => void;
}>;

type NoticeBannerStackItem = Readonly<{
  targetId?: string;
  id?: string;
}> &
  NoticeBannersStackItemProps;

type NoticeBannersStackItemProps = ComponentProps<typeof NoticeBanner>;

export type NoticeBannersStackItems = readonly NoticeBannerStackItem[];
